import type { ScenePlan, ScenePlanSignature } from "./scene-plan.js";
import { slantDeltaX } from "./scene-geometry.js";

export interface SceneBounds {
  readonly width: number;
  readonly height: number;
  readonly pixelRatio: number;
}

export interface PaperGrainPattern {
  readonly kind: "horizontal-washi-fiber-rows";
  readonly rowHeightPx: 1;
}

export const PAPER_GRAIN_PATTERN: Readonly<PaperGrainPattern> = Object.freeze({
  kind: "horizontal-washi-fiber-rows",
  rowHeightPx: 1,
});

export interface InterpretedRainSegment {
  readonly anchorX: number;
  readonly startX: number;
  readonly startY: number;
  readonly endX: number;
  readonly endY: number;
  readonly length: number;
  readonly alpha: number;
  readonly phase: number;
  readonly speed: number;
  readonly cycleLength: number;
}

export interface InterpretedRipple {
  readonly centerX: number;
  readonly centerY: number;
  readonly progress: number;
  readonly radius: number;
  readonly alpha: number;
  readonly phase: number;
  readonly lifeMs: number;
}

export interface MutableInterpretedRainSegment {
  anchorX: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  length: number;
  alpha: number;
  phase: number;
  speed: number;
  cycleLength: number;
}

export interface MutableInterpretedRipple {
  centerX: number;
  centerY: number;
  progress: number;
  radius: number;
  alpha: number;
  phase: number;
  lifeMs: number;
}

export interface SceneSemanticBuffer {
  signature: ScenePlanSignature;
  timeMs: number;
  bounds: {
    width: number;
    height: number;
    pixelRatio: number;
  };
  paper: {
    color: string;
    grain: number;
    grainColor: string;
    grainOpacity: number;
    grainScalePx: number;
    pattern: Readonly<PaperGrainPattern>;
  };
  wash: {
    color: string;
    opacity: number;
    coverage: number;
    feather: 0;
    rect: { x: number; y: number; width: number; height: number };
  };
  rain: MutableInterpretedRainSegment[];
  ripples: MutableInterpretedRipple[];
}

export interface SceneRenderState {
  readonly signature: ScenePlanSignature;
  readonly timeMs: number;
  readonly bounds: SceneBounds;
  readonly paper: Readonly<{
    color: string;
    grain: number;
    grainColor: string;
    grainOpacity: number;
    grainScalePx: number;
    pattern: Readonly<PaperGrainPattern>;
  }>;
  readonly wash: Readonly<{
    color: string;
    opacity: number;
    coverage: number;
    feather: 0;
    rect: Readonly<{ x: number; y: number; width: number; height: number }>;
  }>;
  readonly rain: readonly InterpretedRainSegment[];
  readonly ripples: readonly InterpretedRipple[];
}

function positiveModulo(value: number, modulus: number): number {
  const remainder = value % modulus;
  return remainder < 0 ? remainder + modulus : remainder;
}

export function interpretRainSegment(
  primitive: ScenePlan["rain"]["primitives"][number],
  slant: number,
  bounds: SceneBounds,
  timeMs: number,
): InterpretedRainSegment {
  const anchorX = primitive.x * bounds.width;
  const cycleLength = bounds.height + primitive.length * 2;
  const distance = positiveModulo(
    primitive.phase * cycleLength + timeMs * primitive.speed / 1000,
    cycleLength,
  );
  const startY = distance - primitive.length;
  const startX = anchorX;
  return Object.freeze({
    anchorX,
    startX,
    startY,
    endX: startX + slantDeltaX(slant, primitive.length, bounds.width, bounds.height),
    endY: startY + primitive.length,
    length: primitive.length,
    alpha: primitive.alpha,
    phase: primitive.phase,
    speed: primitive.speed,
    cycleLength,
  });
}

function createMutableRainSegment(): MutableInterpretedRainSegment {
  return {
    anchorX: 0,
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
    length: 0,
    alpha: 0,
    phase: 0,
    speed: 0,
    cycleLength: 0,
  };
}

function createMutableRipple(): MutableInterpretedRipple {
  return {
    centerX: 0,
    centerY: 0,
    progress: 0,
    radius: 0,
    alpha: 0,
    phase: 0,
    lifeMs: 0,
  };
}

export function resizeSceneSemanticBuffer(
  buffer: SceneSemanticBuffer,
  plan: Pick<ScenePlan, "rain" | "ripples">,
): SceneSemanticBuffer {
  const rainCount = plan.rain.primitives.length;
  while (buffer.rain.length < rainCount) buffer.rain.push(createMutableRainSegment());
  buffer.rain.length = rainCount;

  const rippleCount = plan.ripples.primitives.length;
  while (buffer.ripples.length < rippleCount) buffer.ripples.push(createMutableRipple());
  buffer.ripples.length = rippleCount;
  return buffer;
}

export function createSceneSemanticBuffer(plan: ScenePlan): SceneSemanticBuffer {
  const buffer: SceneSemanticBuffer = {
    signature: plan.signature,
    timeMs: 0,
    bounds: { width: 0, height: 0, pixelRatio: 0 },
    paper: {
      color: "",
      grain: 0,
      grainColor: "",
      grainOpacity: 0,
      grainScalePx: 0,
      pattern: PAPER_GRAIN_PATTERN,
    },
    wash: {
      color: "",
      opacity: 0,
      coverage: 0,
      feather: 0,
      rect: { x: 0, y: 0, width: 0, height: 0 },
    },
    rain: [],
    ripples: [],
  };
  return resizeSceneSemanticBuffer(buffer, plan);
}

/**
 * Writes renderer-neutral scene semantics into reusable storage.
 * The steady-state path allocates nothing; primitive storage grows only when counts increase.
 */
export function writeScenePlanToSemanticBuffer(
  buffer: SceneSemanticBuffer,
  plan: ScenePlan,
  bounds: SceneBounds,
  timeMs: number,
): SceneSemanticBuffer {
  if (buffer.rain.length !== plan.rain.primitives.length
      || buffer.ripples.length !== plan.ripples.primitives.length) {
    resizeSceneSemanticBuffer(buffer, plan);
  }

  buffer.signature = plan.signature;
  buffer.timeMs = timeMs;
  buffer.bounds.width = bounds.width;
  buffer.bounds.height = bounds.height;
  buffer.bounds.pixelRatio = bounds.pixelRatio;

  buffer.paper.color = plan.paper.color;
  buffer.paper.grain = plan.paper.grain;
  buffer.paper.grainColor = plan.palette.background;
  buffer.paper.grainOpacity = plan.paper.grain * 0.16;
  buffer.paper.grainScalePx = 8 - plan.paper.grain * 4;
  buffer.paper.pattern = PAPER_GRAIN_PATTERN;

  const washWidth = bounds.width * plan.wash.coverage;
  const washHeight = bounds.height * plan.wash.coverage;
  buffer.wash.color = plan.wash.color;
  buffer.wash.opacity = plan.wash.opacity;
  buffer.wash.coverage = plan.wash.coverage;
  buffer.wash.feather = 0;
  buffer.wash.rect.x = (bounds.width - washWidth) / 2;
  buffer.wash.rect.y = (bounds.height - washHeight) / 2;
  buffer.wash.rect.width = washWidth;
  buffer.wash.rect.height = washHeight;

  for (let index = 0; index < buffer.rain.length; index += 1) {
    const primitive = plan.rain.primitives[index];
    const rain = buffer.rain[index];
    const anchorX = primitive.x * bounds.width;
    const cycleLength = bounds.height + primitive.length * 2;
    const distance = positiveModulo(
      primitive.phase * cycleLength + timeMs * primitive.speed / 1000,
      cycleLength,
    );
    const startY = distance - primitive.length;
    rain.anchorX = anchorX;
    rain.startX = anchorX;
    rain.startY = startY;
    rain.endX = anchorX + slantDeltaX(
      plan.rain.slant,
      primitive.length,
      bounds.width,
      bounds.height,
    );
    rain.endY = startY + primitive.length;
    rain.length = primitive.length;
    rain.alpha = primitive.alpha;
    rain.phase = primitive.phase;
    rain.speed = primitive.speed;
    rain.cycleLength = cycleLength;
  }

  for (let index = 0; index < buffer.ripples.length; index += 1) {
    const primitive = plan.ripples.primitives[index];
    const ripple = buffer.ripples[index];
    const progress = positiveModulo(primitive.phase + timeMs / primitive.lifeMs, 1);
    ripple.centerX = primitive.x * bounds.width;
    ripple.centerY = primitive.y * bounds.height;
    ripple.progress = progress;
    ripple.radius = primitive.radius * progress;
    ripple.alpha = primitive.alpha * (1 - progress);
    ripple.phase = primitive.phase;
    ripple.lifeMs = primitive.lifeMs;
  }
  return buffer;
}

function freezeSceneSemanticBuffer(buffer: SceneSemanticBuffer): SceneRenderState {
  Object.freeze(buffer.bounds);
  Object.freeze(buffer.paper);
  Object.freeze(buffer.wash.rect);
  Object.freeze(buffer.wash);
  for (const rain of buffer.rain) Object.freeze(rain);
  Object.freeze(buffer.rain);
  for (const ripple of buffer.ripples) Object.freeze(ripple);
  Object.freeze(buffer.ripples);
  return Object.freeze(buffer);
}

/**
 * The only renderer-neutral interpretation of signed ScenePlan v1 visual fields.
 * V1 has no signed wash-feather field, so its exact feather is a hard edge (zero).
 */
export function interpretScenePlan(
  plan: ScenePlan,
  bounds: SceneBounds,
  timeMs: number,
): SceneRenderState {
  const buffer = createSceneSemanticBuffer(plan);
  writeScenePlanToSemanticBuffer(buffer, plan, bounds, timeMs);
  return freezeSceneSemanticBuffer(buffer);
}
